// Pipeline Copilot — the Explain panel family. Port of the approved Claude
// Design bundle (pipeline-copilot/cp-explain.jsx + cp-chart.jsx): one visual
// system answers all four explain anchors (chart bucket, alert, trace,
// ingest run). Every claim carries clickable evidence chips; AI output is
// labeled `AI-GENERATED · RA` (CP_MODEL_LABEL) and never styled like a
// system verdict —
// the deterministic next-step pill carries its own `SYSTEM · NOT AI` tag.
//
// The mock's simulated POST (useExplain sim) maps to the real
// POST /copilot/explain mutation via useExplainPanel; the mock's forced-state
// tweaks (window.cpForceState) are design-tool chrome and are not ported.
// All affordances gate on CopilotStatus.enabled AT THE CALL SITE — a screen
// without status data renders nothing copilot-related (no teasers).

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@/components/primitives';
import { ApiError } from '@/api/errors';
import { useExplain } from '@/api/hooks';
import {
  CP_MODEL_LABEL, chipAction, clockLabel, formatAge, hasServerVisuals, parseRank, resolvableClaims,
  skeletonHeight, splitNextStep, verdictTone,
} from '@/lib/copilot';
import type { SkeletonKind } from '@/lib/copilot';
import { withEt } from '@/lib/datetime';
import { CPStageDiagram, CPVisualStrip } from '@/components/copilot-visuals';
import type { EvidenceItem, ExplainClaim, ExplainRequest, ExplainResponse } from '@/api/types.gen';

// ── icons (house 1.4–1.5 stroke style via the primitives Icon wrapper) ──────

export function CPSparkle({ size = 11, style }: { size?: number; style?: CSSProperties }) {
  return (
    <Icon size={size} sw={1.4} style={style}
      d={<><path d="M7 2.6 8.3 6.2 11.9 7.5 8.3 8.8 7 12.4 5.7 8.8 2.1 7.5 5.7 6.2 Z"/><path d="M12.6 10.6v3M11.1 12.1h3"/></>}/>
  );
}

export function CPRegenIcon({ size = 12 }: { size?: number }) {
  return (
    <Icon size={size} sw={1.5}
      d={<><path d="M13.2 8A5.2 5.2 0 1 1 11.6 4.3"/><path d="M13.4 2.4v2.8h-2.8"/></>}/>
  );
}

function CPXIcon() {
  return <Icon size={11} sw={1.5} d={<path d="m3.5 3.5 9 9M12.5 3.5l-9 9"/>}/>;
}

function CPGoIcon() {
  return <Icon size={8} sw={2} d={<path d="M4.5 11.5 11.5 4.5M6 4.5h5.5V10"/>}/>;
}

// ── house hover tooltip (fixed, clamped) ────────────────────────────────────

interface CpTipState {
  x: number;
  y: number;
  node: ReactNode;
}

export interface CpTipApi {
  show: (e: ReactMouseEvent<HTMLElement>, node: ReactNode) => void;
  hide: () => void;
  tipEl: ReactNode;
}

export function useCpTip(): CpTipApi {
  const [tip, setTip] = useState<CpTipState | null>(null);
  const show = (e: ReactMouseEvent<HTMLElement>, node: ReactNode) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ x: Math.min(Math.max(8, r.left), window.innerWidth - 330), y: r.top, node });
  };
  const hide = () => setTip(null);
  const tipEl = tip ? (
    <div className="cp-tip" style={{ left: tip.x, top: tip.y, transform: 'translateY(calc(-100% - 7px))' }}>
      {tip.node}
    </div>
  ) : null;
  return { show, hide, tipEl };
}

const CP_KIND_LABEL: Record<string, string> = {
  series: 'metric series',
  run: 'ingest run',
  alert: 'alert',
  log_group: 'error group',
  log_line: 'log line',
  heartbeat: 'heartbeat',
  stat: 'derived stat',
  metric: 'metric',
  delta: 'delta',
  log: 'log',
};

// ── evidence chip — carries words; hover = value; click = link | scroll ─────

export function CPChip({ ev, tipApi, onScrollTo, hov = false, scrollNote = null }: {
  ev: EvidenceItem;
  tipApi: CpTipApi;
  onScrollTo?: (ev: EvidenceItem) => void;
  hov?: boolean;
  /**
   * When set, SCROLL chips render inert (static dotted, no scroll/highlight)
   * carrying this title — positional line mapping is unavailable (e.g. the
   * trace exceeds the /admin/logs fetch cap). Link chips are unaffected.
   */
  scrollNote?: string | null;
}) {
  const scroll = chipAction(ev) === 'scroll';
  const inert = scroll && scrollNote != null;
  const body = (
    <>
      <span className="cp-tip-k">{CP_KIND_LABEL[ev.kind] ?? ev.kind}</span>
      {ev.value}
      <span className="cp-tip-route">{scroll ? (inert ? '§ ' + scrollNote : '§ scrolls to the cited line') : '↗ ' + ev.link}</span>
    </>
  );
  const className = 'cp-chip'
    + (scroll ? (inert ? ' cp-chip-static' : ' cp-chip-scroll') : '')
    + (hov ? ' cp-chip-hov' : '');
  const contents = (
    <>
      <span className="cp-chip-lbl">{ev.label}</span>
      {!scroll && <span className="cp-chip-go"><CPGoIcon/></span>}
    </>
  );

  // Evidence links are internal app routes minted by the server. Rendering a
  // real router Link lets route-intent hover, keyboard focus, and touch intent
  // run the destination loader before navigation. Scroll citations remain
  // buttons because they act on the current document.
  if (!scroll && ev.link) {
    return (
      <Link
        to={ev.link as never}
        preload="intent"
        className={className}
        onMouseEnter={(e) => tipApi.show(e, body)}
        onMouseLeave={tipApi.hide}
        onClick={(e) => {
          e.stopPropagation();
          tipApi.hide();
        }}
      >
        {contents}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      title={inert ? (scrollNote ?? undefined) : undefined}
      onMouseEnter={(e) => tipApi.show(e, body)}
      onMouseLeave={tipApi.hide}
      onClick={(e) => {
        e.stopPropagation();
        tipApi.hide();
        if (!inert) onScrollTo?.(ev);
      }}
    >
      {contents}
    </button>
  );
}

// ── one claim: sentence + its chips inline at the end ───────────────────────

export function CPClaim({ claim, idx = 0, evidence, ranked = false, tipApi, onScrollTo, onVisualScroll, hovered = false, onHover, chipHov = false, scrollNote = null }: {
  claim: ExplainClaim;
  idx?: number;
  evidence: ReadonlyArray<EvidenceItem>;
  ranked?: boolean;
  tipApi: CpTipApi;
  onScrollTo?: (ev: EvidenceItem) => void;
  /** Scroll to the server-built visual this claim anchors (claim.visual_ref). */
  onVisualScroll?: (visualId: string) => void;
  hovered?: boolean;
  onHover?: (idx: number | null) => void;
  chipHov?: boolean;
  scrollNote?: string | null;
}) {
  const { rank, text } = ranked ? parseRank(claim.text) : { rank: null, text: claim.text };
  // Callers pre-filter via resolvableClaims; this find keeps render safe anyway.
  const refs = claim.refs
    .map((id) => evidence.find((ev) => ev.id === id))
    .filter((ev): ev is EvidenceItem => !!ev);
  const inner = (
    <>
      {text}
      <span style={{ whiteSpace: 'normal' }}>
        {refs.map((ev) => (
          <CPChip key={ev.id} ev={ev} tipApi={tipApi} onScrollTo={onScrollTo} hov={chipHov && hovered} scrollNote={scrollNote}/>
        ))}
        {claim.visual_ref && onVisualScroll && (
          <button type="button" className="cp-chip cp-chip-visual"
            onClick={(e) => { e.stopPropagation(); onVisualScroll(claim.visual_ref!); }}>
            see chart
          </button>
        )}
      </span>
    </>
  );
  const cls = 'cp-claim'
    + (ranked ? ' cp-ranked cp-r' + Math.min(idx + 1, 3) : '')
    + (hovered ? ' cp-hov' : '');
  return (
    <div
      className={cls}
      onMouseEnter={onHover ? () => onHover(idx) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
    >
      {ranked ? <><span className="cp-rank">{rank}</span><span>{inner}</span></> : inner}
    </div>
  );
}

// ── AI footer strip ─────────────────────────────────────────────────────────

const VERIFIED_TIP_TEXT =
  'Every number in this explanation appears in the underlying data — the model cannot invent or compute figures.';

function CPFooter({ resp, onRegenerate, regenBusy, tipApi }: {
  resp: ExplainResponse;
  onRegenerate?: () => void;
  regenBusy?: boolean;
  tipApi: CpTipApi;
}) {
  const verifiedTip = (
    <>
      <span className="cp-tip-k">numbers verified</span>
      {VERIFIED_TIP_TEXT}
    </>
  );
  const serverBuiltTip = (
    <>
      <span className="cp-tip-k">server-built</span>
      The stage verdict and charts are computed by the server from the data — not written by the model.
    </>
  );
  return (
    <div className="cp-foot">
      <span className="cp-ai"><CPSparkle size={10}/>AI-GENERATED · {resp.degraded ? `${CP_MODEL_LABEL} UNAVAILABLE` : CP_MODEL_LABEL}</span>
      {resp.confidence === 'low' && !resp.degraded && <span className="cp-hypo-tag">HYPOTHESIS</span>}
      {!resp.degraded && (
        <>
          <span className="cp-sep">·</span>
          <span className="cp-verified" onMouseEnter={(e) => tipApi.show(e, verifiedTip)} onMouseLeave={tipApi.hide}>
            Numbers verified
          </span>
        </>
      )}
      {hasServerVisuals(resp) && (
        <>
          <span className="cp-sep">·</span>
          <span className="cp-server-built"
            onMouseEnter={(e) => tipApi.show(e, serverBuiltTip)} onMouseLeave={tipApi.hide}>
            Charts server-built
          </span>
        </>
      )}
      <span className="cp-sep">·</span>
      <span>{withEt(clockLabel(resp.generated_at))}</span>
      {resp.cached && (
        <>
          <span className="cp-sep">·</span>
          <span className="cp-cached">Cached · {formatAge(resp.generated_at, Date.now())}</span>
        </>
      )}
      {onRegenerate && (
        <button
          type="button"
          className="cp-regen"
          title="Regenerate (forces a fresh generation)"
          disabled={regenBusy}
          onClick={onRegenerate}
        >
          <CPRegenIcon/>
        </button>
      )}
    </div>
  );
}

// ── two-stage loading skeleton — reserves final height, no spinners ─────────

export function CPSkeleton({ kind, stage, evCount }: {
  kind: SkeletonKind;
  stage: 1 | 2;
  /** Only when known up front (the live POST answers whole — count arrives with the response). */
  evCount?: number;
}) {
  return (
    <div className="cp-panel cp-in cp-skel" style={{ minHeight: skeletonHeight(kind) }}>
      <div className="cp-stage">
        {stage >= 2 ? (
          <div className="cp-stage-row cp-done">
            <span className="cp-stage-mark">✓</span>Evidence assembled{evCount != null ? ` · ${evCount} items` : ''}
          </div>
        ) : (
          <div className="cp-stage-row cp-now"><span className="cp-stage-mark"/>Assembling evidence…</div>
        )}
        {stage >= 2 ? (
          <div className="cp-stage-row cp-now"><span className="cp-stage-mark"/>Asking {CP_MODEL_LABEL}…</div>
        ) : (
          <div className="cp-stage-row cp-pend"><span className="cp-stage-mark">–</span>Asking {CP_MODEL_LABEL}</div>
        )}
      </div>
      <div className="cp-ghosts">
        <div className="cp-ghost" style={{ width: '58%', height: 13 }}/>
        <div className="cp-ghost" style={{ width: '86%' }}/>
        <div className="cp-ghost" style={{ width: '74%' }}/>
      </div>
    </div>
  );
}

/** Sequences the two loading micro-labels: stage 1 → stage 2 after ~0.9s. */
export function useSkeletonStage(active: boolean): 1 | 2 {
  const [stage, setStage] = useState<1 | 2>(1);
  useEffect(() => {
    setStage(1);
    if (!active) return undefined;
    const t = window.setTimeout(() => setStage(2), 900);
    return () => window.clearTimeout(t);
  }, [active]);
  return stage;
}

// ── inline error (house mutation-error idiom) — 400/404 map honestly ────────

export function CPErrorLine({ error, label = 'EXPLAIN FAILED', onRetry }: {
  error?: unknown;
  label?: string;
  onRetry?: () => void;
}) {
  const detail = error instanceof ApiError
    ? `${error.status} ${error.code}${error.message ? ' — ' + error.message : ''}`
    : `${CP_MODEL_LABEL} DID NOT RESPOND`;
  return (
    <div className="cp-panel cp-in">
      <div className="cp-errline">
        <span>● {label} — {detail}</span>
        {onRetry && <button type="button" className="btn btn-sm cp-retry" onClick={onRetry}>Retry</button>}
      </div>
    </div>
  );
}

// ── the Explain panel ────────────────────────────────────────────────────────

export type CPPanelState = 'loading' | 'done' | 'error';

export function CPPanel({ resp, state, kind, error, onClose, onRegenerate, regenBusy, onScrollTo, onClaimHover, hoveredClaim, chipHov = false, scrollNote = null }: {
  resp: ExplainResponse | null;
  state: CPPanelState;
  /** Skeleton height key while loading (resp not yet known). */
  kind: SkeletonKind;
  error?: unknown;
  onClose?: () => void;
  onRegenerate?: () => void;
  regenBusy?: boolean;
  onScrollTo?: (ev: EvidenceItem) => void;
  onClaimHover?: (idx: number | null) => void;
  hoveredClaim?: number | null;
  chipHov?: boolean;
  /** Renders scroll chips inert with this title (see CPChip.scrollNote). */
  scrollNote?: string | null;
}) {
  const tipApi = useCpTip();
  const stage = useSkeletonStage(state === 'loading');
  const panelRef = useRef<HTMLDivElement>(null);
  if (state === 'loading') return <CPSkeleton kind={kind} stage={stage}/>;
  if (state === 'error') return <CPErrorLine error={error} onRetry={onRegenerate}/>;
  if (!resp) return null;

  // The render guard: a claim with a dangling citation never renders.
  const claims = resolvableClaims(resp.claims, resp.evidence);
  const ranked = resp.kind === 'alert' && !resp.degraded;
  const low = resp.confidence === 'low' && !resp.degraded;
  const anyHover = hoveredClaim != null && hoveredClaim >= 0;
  const next = resp.next_step ? splitNextStep(resp.next_step.text) : null;

  // Scroll the anchored server-built chart into view (claim "see chart" chip).
  const scrollToVisual = (id: string) => {
    panelRef.current
      ?.querySelector(`[data-cp-visual="${CSS.escape(id)}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  return (
    <div ref={panelRef} className={'cp-panel cp-in' + (low ? ' cp-low' : '')}>
      {onClose && <button type="button" className="cp-x" title="Close" onClick={onClose}><CPXIcon/></button>}

      {/* The deterministic "where" answer reads first — and survives degraded,
          since the verdict + charts are server-built (trustworthy without the
          model). */}
      {resp.verdict && <CPStageDiagram verdict={resp.verdict} tipApi={tipApi}/>}

      {resp.degraded ? (
        <>
          <div className="cp-degraded-note"><span className="d"/>Model unavailable — showing server-built evidence</div>
          <CPVisualStrip visuals={resp.visuals}/>
          <div className="cp-evgrid">
            {resp.evidence.map((ev) => (
              <div className="cp-evcell" key={ev.id}>
                <CPChip ev={ev} tipApi={tipApi} onScrollTo={onScrollTo} scrollNote={scrollNote}/>
                <span className="cp-evval">{ev.value}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <h4 className="cp-headline">{resp.headline}</h4>
          <div className={'cp-claims' + (anyHover ? ' cp-focus' : '')}>
            {claims.map((c, i) => (
              <CPClaim key={i} claim={c} idx={i} evidence={resp.evidence} ranked={ranked}
                tipApi={tipApi} onScrollTo={onScrollTo} onVisualScroll={scrollToVisual}
                chipHov={chipHov} scrollNote={scrollNote}
                hovered={hoveredClaim === i} onHover={onClaimHover}/>
            ))}
          </div>
          <CPVisualStrip visuals={resp.visuals}/>
          {resp.next_step && next && (
            <div className="cp-next">
              <span className={'cp-next-pill ' + verdictTone(resp.next_step.text)}>
                <span className="d"/>{next.pill}
              </span>
              <span className="cp-next-txt">{next.rest}</span>
              <span className="cp-next-src">System · not AI</span>
            </div>
          )}
        </>
      )}

      <CPFooter resp={resp} onRegenerate={onRegenerate} regenBusy={regenBusy} tipApi={tipApi}/>
      {tipApi.tipEl}
    </div>
  );
}

// ── ✨ trigger button ────────────────────────────────────────────────────────

export function CPSparkBtn({ label, onClick, open = false, disabled = false, style }: {
  label: string;
  onClick: () => void;
  open?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      className={'cp-spark-btn' + (open ? ' cp-open' : '')}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      style={style}
    >
      <CPSparkle size={10}/>{label}
    </button>
  );
}

// ── one explain instance per anchor (the real POST behind the panel) ────────

export interface ExplainPanelApi {
  /** False ⇒ render nothing (the anchor hasn't been triggered / was closed). */
  open: boolean;
  state: CPPanelState;
  resp: ExplainResponse | null;
  error: unknown;
  trigger: (req: ExplainRequest) => void;
  /** Repeats the last request with force=true (drops the server cache). */
  regenerate: () => void;
  close: () => void;
}

export function useExplainPanel(): ExplainPanelApi {
  const mut = useExplain();
  const [wanted, setWanted] = useState(false);
  const lastReq = useRef<ExplainRequest | null>(null);
  // idle-after-trigger renders as loading (the mutation flips to pending on
  // the same commit) so the panel never flashes empty.
  const state: CPPanelState = mut.isError ? 'error' : mut.isSuccess ? 'done' : 'loading';
  return {
    open: wanted,
    state,
    resp: mut.data ?? null,
    error: mut.error,
    trigger: (req) => {
      lastReq.current = req;
      setWanted(true);
      mut.mutate({ request: req });
    },
    regenerate: () => {
      const req = lastReq.current;
      if (req) mut.mutate({ request: req, force: true });
    },
    close: () => {
      setWanted(false);
      lastReq.current = null;
      mut.reset();
    },
  };
}

// ── chart-bucket dock (rendered below the host chart block) ─────────────────

export interface ChartExplainSel {
  /** Which chart block hosts the dock ('vol-web' | 'vol-tix' | 'quality'). */
  host: string;
  /** Human series label for the scope line. */
  metric: string;
  bucketLabel: string;
  /** Built from the REAL history query's lane/metric/bucket/granularity. */
  request: ExplainRequest;
  /** Monotonic tick — a re-click on another bucket restarts the POST. */
  ts: number;
}

export function CPChartDock({ sel, onClose }: { sel: ChartExplainSel; onClose: () => void }) {
  const panel = useExplainPanel();
  const trigger = useRef(panel.trigger);
  trigger.current = panel.trigger;
  useEffect(() => {
    trigger.current(sel.request);
  }, [sel.ts, sel.request]);
  if (!panel.open) return null;

  return (
    <div className="cp-dock">
      <div className="cp-scope">
        <CPSparkle size={9}/><span>Explain</span><span>·</span>
        <b>{sel.metric}</b><span>·</span><b>bucket {sel.bucketLabel}</b>
      </div>
      <CPPanel
        resp={panel.resp} state={panel.state} kind="chart_point" error={panel.error}
        onClose={() => { panel.close(); onClose(); }}
        onRegenerate={panel.regenerate}
        regenBusy={panel.state === 'loading'}
      />
    </div>
  );
}
